// /weighbridge/settings/cameras/:enc_id — Phase C2 Group 2.
//
// Create + edit form for a single camera. Server deviations override the
// C2 prompt (see WEIGHBRIDGE_PHASE_C2 spec) — type is the 4-value enum,
// snapshot lives in a single string template, connection field is
// `connection_string_encrypted` (plaintext on the wire; server encrypts).
//
// yard_id is REQUIRED and immutable after create. weighbridge_id is
// optional but, when selected, we auto-fill the yard from the chosen
// bridge's yard so the two fields stay consistent. The yard select is
// disabled on edit because the server rejects yard_id on PATCH.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import {
  useCreateCamera,
  useDeleteCamera,
  useUpdateCamera,
  useWbCamera,
  type CreateCameraBody,
  type UpdateCameraBody,
  type WbCameraProtocol,
  type WbCameraType,
} from '../hooks/useWbCameras';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbCameraDetailPage.module.css';

// -- Minimal local types for the yard / weighbridge selects --------------
// Phase C1 owns the real hooks; here we just hit the list endpoints
// directly because Group 2 does not own those files.

interface YardSummary {
  enc_id: string;
  name: string;
}

interface WeighbridgeSummary {
  enc_id: string;
  name: string;
  yard: { enc_id: string; name: string };
}

const TYPE_OPTIONS: ReadonlyArray<{ value: WbCameraType; label: string }> = [
  { value: 'overview', label: 'Overview' },
  { value: 'anpr', label: 'ANPR' },
  { value: 'material', label: 'Material' },
  { value: 'plate', label: 'Plate' },
];

const PROTOCOL_OPTIONS: ReadonlyArray<{ value: WbCameraProtocol; label: string }> = [
  { value: 'rtsp', label: 'RTSP' },
  { value: 'onvif', label: 'ONVIF' },
  { value: 'http_mjpeg', label: 'HTTP MJPEG' },
  { value: 'usb', label: 'USB' },
];

const CONNECTION_FIELD: Record<WbCameraProtocol, { label: string; placeholder: string }> = {
  rtsp: { label: 'RTSP URL', placeholder: 'rtsp://192.168.1.100/stream' },
  onvif: { label: 'ONVIF Endpoint', placeholder: 'http://192.168.1.100/onvif/device_service' },
  http_mjpeg: { label: 'MJPEG URL', placeholder: 'http://192.168.1.100/mjpg/video.mjpg' },
  usb: { label: 'Device path / index', placeholder: '/dev/video0 or 0' },
};

interface FormState {
  name: string;
  type: WbCameraType;
  protocol: WbCameraProtocol;
  yard_id: string;
  weighbridge_id: string;
  connection_string: string;
  snapshot_path_template: string;
  is_active: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  type: 'overview',
  protocol: 'rtsp',
  yard_id: '',
  weighbridge_id: '',
  connection_string: '',
  snapshot_path_template: '',
  is_active: true,
};

export function WbCameraDetailPage(): JSX.Element {
  const navigate = useNavigate();
  const { enc_id } = useParams<{ enc_id: string }>();
  const isNew = enc_id === 'new' || !enc_id;
  const encIdForEdit = isNew ? '' : enc_id;

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const cameraQuery = useWbCamera(encIdForEdit || undefined);
  const createCamera = useCreateCamera();
  const updateCamera = useUpdateCamera(encIdForEdit);
  const deleteCamera = useDeleteCamera(encIdForEdit);

  // Hydrate the form once the camera detail lands (edit mode).
  useEffect(() => {
    const camera = cameraQuery.data?.camera;
    if (!camera) return;
    setForm({
      name: camera.name,
      type: camera.type,
      protocol: camera.protocol,
      yard_id: camera.yard.enc_id,
      weighbridge_id: camera.weighbridge?.enc_id ?? '',
      connection_string: '',
      snapshot_path_template: camera.snapshot_path_template ?? '',
      is_active: camera.is_active,
    });
  }, [cameraQuery.data?.camera]);

  // -- Yard + weighbridge dropdowns ----------------------------------------
  const yardsQuery = useQuery({
    queryKey: queryKeys.wb.yards(),
    queryFn: async () => {
      const res = await apiClient.get<{ yards: YardSummary[] }>(endpoints.wb.yards);
      return res.data;
    },
  });
  const bridgesQuery = useQuery({
    queryKey: queryKeys.wb.weighbridges(),
    queryFn: async () => {
      const res = await apiClient.get<{ weighbridges: WeighbridgeSummary[] }>(
        endpoints.wb.weighbridges,
      );
      return res.data;
    },
  });

  const yards = yardsQuery.data?.yards ?? [];
  const bridges = bridgesQuery.data?.weighbridges ?? [];

  // When a weighbridge is selected, auto-fill the yard from its parent
  // yard. The yard select stays editable in create mode so the operator
  // can still pick a yard with no bridges attached.
  function handleBridgeChange(nextBridgeEnc: string) {
    setForm((prev) => {
      if (nextBridgeEnc === '') return { ...prev, weighbridge_id: '' };
      const bridge = bridges.find((b) => b.enc_id === nextBridgeEnc);
      return {
        ...prev,
        weighbridge_id: nextBridgeEnc,
        yard_id: bridge?.yard.enc_id ?? prev.yard_id,
      };
    });
  }

  const connection = CONNECTION_FIELD[form.protocol];
  const subtitle = useMemo(
    () => (isNew ? 'Add a new camera.' : 'Edit camera configuration.'),
    [isNew],
  );

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    if (!form.yard_id) {
      setError('Yard is required.');
      return;
    }

    if (isNew) {
      const body: CreateCameraBody = {
        yard_id: form.yard_id,
        name: form.name.trim(),
        type: form.type,
        protocol: form.protocol,
        is_active: form.is_active,
      };
      if (form.weighbridge_id) body.weighbridge_id = form.weighbridge_id;
      if (form.connection_string.trim()) {
        body.connection_string_encrypted = form.connection_string.trim();
      }
      if (form.snapshot_path_template.trim()) {
        body.snapshot_path_template = form.snapshot_path_template.trim();
      }
      createCamera.mutate(body, {
        onSuccess: () => navigate('/weighbridge/settings/cameras'),
        onError: (err) => setError(extractErrorMessage(err)),
      });
      return;
    }

    const body: UpdateCameraBody = {
      name: form.name.trim(),
      type: form.type,
      protocol: form.protocol,
      weighbridge_id: form.weighbridge_id ? form.weighbridge_id : null,
      snapshot_path_template: form.snapshot_path_template.trim()
        ? form.snapshot_path_template.trim()
        : null,
      is_active: form.is_active,
    };
    // Only send the connection string when the user re-entered one.
    if (form.connection_string.trim()) {
      body.connection_string_encrypted = form.connection_string.trim();
    }
    updateCamera.mutate(body, {
      onSuccess: () => navigate('/weighbridge/settings/cameras'),
      onError: (err) => setError(extractErrorMessage(err)),
    });
  }

  function handleDelete() {
    deleteCamera.mutate(undefined, {
      onSuccess: () => navigate('/weighbridge/settings/cameras'),
      onError: (err) => {
        setError(extractErrorMessage(err));
        setConfirmOpen(false);
      },
    });
  }

  const saving = createCamera.isPending || updateCamera.isPending;
  const loadingExisting = !isNew && cameraQuery.isLoading;

  return (
    <div className={styles.page}>
      <PageHeader
        title={isNew ? 'New Camera' : 'Edit Camera'}
        subtitle={subtitle}
        actions={
          <Button
            variant="secondary"
            icon={ArrowLeft}
            onClick={() => navigate('/weighbridge/settings/cameras')}
          >
            Back
          </Button>
        }
      />

      {loadingExisting ? (
        <Card>
          <p className={styles.note}>Loading camera…</p>
        </Card>
      ) : (
        <form onSubmit={handleSubmit} className={styles.form}>
          <Card>
            <h2 className={styles.sectionHeading}>General</h2>
            <div className={styles.grid}>
              <label className={styles.field}>
                <span>Name</span>
                <input
                  required
                  maxLength={200}
                  value={form.name}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                />
              </label>
              <label className={styles.field}>
                <span>Type</span>
                <select
                  value={form.type}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      type: e.target.value as WbCameraType,
                    }))
                  }
                >
                  {TYPE_OPTIONS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.field}>
                <span>Protocol</span>
                <select
                  value={form.protocol}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      protocol: e.target.value as WbCameraProtocol,
                    }))
                  }
                >
                  {PROTOCOL_OPTIONS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </Card>

          <Card>
            <h2 className={styles.sectionHeading}>Location</h2>
            <div className={styles.grid}>
              <label className={styles.field}>
                <span>Yard</span>
                <select
                  required
                  value={form.yard_id}
                  disabled={!isNew}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, yard_id: e.target.value }))
                  }
                >
                  <option value="">— Select yard —</option>
                  {yards.map((y) => (
                    <option key={y.enc_id} value={y.enc_id}>
                      {y.name}
                    </option>
                  ))}
                </select>
                {!isNew ? (
                  <p className={styles.helpText}>
                    Yard cannot be changed after the camera is created.
                  </p>
                ) : null}
              </label>
              <label className={styles.field}>
                <span>Assigned bridge (optional)</span>
                <select
                  value={form.weighbridge_id}
                  onChange={(e) => handleBridgeChange(e.target.value)}
                >
                  <option value="">— None —</option>
                  {bridges.map((b) => (
                    <option key={b.enc_id} value={b.enc_id}>
                      {b.name}
                    </option>
                  ))}
                </select>
                <p className={styles.helpText}>
                  Selecting a bridge sets the yard automatically.
                </p>
              </label>
            </div>
          </Card>

          <Card>
            <h2 className={styles.sectionHeading}>Connection</h2>
            <div className={styles.grid}>
              <label className={`${styles.field} ${styles.fullRow}`}>
                <span>{connection.label}</span>
                <input
                  placeholder={connection.placeholder}
                  value={form.connection_string}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      connection_string: e.target.value,
                    }))
                  }
                />
                <p className={styles.helpText}>
                  Stored encrypted server-side; cannot be read back. Re-enter to overwrite.
                </p>
              </label>
            </div>
          </Card>

          <Card>
            <h2 className={styles.sectionHeading}>Snapshots</h2>
            <div className={styles.grid}>
              <label className={`${styles.field} ${styles.fullRow}`}>
                <span>Snapshot path template (optional)</span>
                <input
                  maxLength={500}
                  value={form.snapshot_path_template}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      snapshot_path_template: e.target.value,
                    }))
                  }
                  placeholder="yard/{yard_code}/bridge/{bridge_code}/{ticket}/gross.jpg"
                />
                <p className={styles.helpText}>
                  Used by the agent to organise snapshot uploads.
                </p>
              </label>
            </div>
          </Card>

          <Card>
            <h2 className={styles.sectionHeading}>Status</h2>
            <label className={styles.toggleRow}>
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, is_active: e.target.checked }))
                }
              />
              <span>Active</span>
            </label>
          </Card>

          {error ? <p className={styles.error}>{error}</p> : null}

          <div className={styles.formActions}>
            {!isNew ? (
              <Button
                type="button"
                variant="danger"
                icon={Trash2}
                onClick={() => setConfirmOpen(true)}
                disabled={saving || deleteCamera.isPending}
              >
                Delete
              </Button>
            ) : null}
            <div className={styles.formActionsSpacer} />
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/weighbridge/settings/cameras')}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      )}

      <ConfirmModal
        open={confirmOpen}
        title="Delete camera?"
        message="This camera will be soft-deleted. You can recreate it later if needed."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
        loading={deleteCamera.isPending}
      />
    </div>
  );
}
